import React, { useEffect, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdConfirmDialog,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { draftDischargeSummary, saveDischargeSummary } from "../api/emergencyApi";
import { escapeHtml, printDocument } from "../utils/print";

/**
 * The discharge summary (ER-DSP-013/014/015).
 *
 * The patient reads this, so the section headings are the questions they would
 * ask, in the order they would ask them — not the clinical record's headings.
 *
 * Drafted from the visit rather than typed from scratch, then edited. The
 * regenerate button is destructive to those edits, so it is behind a
 * confirmation whenever there are edits to lose and plain when there are not.
 */
const SECTIONS = [
  { key: "why_you_came", label: "Why you came in", rows: 3 },
  { key: "what_we_found", label: "What we found", rows: 3 },
  { key: "what_we_did", label: "What we did", rows: 3 },
  { key: "what_to_do_next", label: "What to do next", rows: 4 },
];

const draftFrom = (summary) =>
  SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: summary?.[s.key] || "" }), {});

export default function DischargeSummaryCard({ visit, onChanged, disabled }) {
  const summary = visit.discharge_summary || null;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => draftFrom(summary));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  useEffect(() => {
    setDraft(draftFrom(summary));
  }, [summary]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onChanged?.();
      return true;
    } catch (e) {
      setError(e?.message || "Something went wrong");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    run(async () => {
      await draftDischargeSummary(visit.id);
      setEditing(false);
    });

  const save = () =>
    run(async () => {
      const payload = SECTIONS.reduce(
        (acc, s) => ({ ...acc, [s.key]: draft[s.key].trim() }),
        {}
      );
      await saveDischargeSummary(visit.id, payload);
      setEditing(false);
    });

  const print = () => {
    const opened = printDocument({
      title: "Emergency Department — Discharge Summary",
      subtitle: `${visit.display_name} · ${visit.visit_number}`,
      body: `
        <h2>Your visit</h2>
        <table class="kv">
          <tr><th>Name</th><td>${escapeHtml(visit.display_name)}</td></tr>
          <tr><th>Hospital number</th><td>${escapeHtml(visit.patient_id)}</td></tr>
          <tr><th>Visit number</th><td>${escapeHtml(visit.visit_number)}</td></tr>
          <tr><th>Seen on</th><td>${escapeHtml(formatDateTime(visit.arrived_at))}</td></tr>
        </table>
        ${SECTIONS.filter((s) => summary?.[s.key])
          .map(
            (s) =>
              `<h2>${escapeHtml(s.label)}</h2><p>${escapeHtml(summary[s.key])
                .replace(/\n/g, "<br/>")}</p>`
          )
          .join("")}
        <div class="sig"><div>Patient / attendant signature</div><div>Staff signature</div></div>`,
    });
    if (!opened) setError("The print window was blocked — allow pop-ups for this site.");
  };

  const anyContent = SECTIONS.some((s) => draft[s.key].trim());

  /* ------------------------------------------------------------ no summary */
  if (!summary && !editing) {
    return (
      <IpdSectionCard
        title="Discharge summary"
        subtitle="What the patient takes home — required before closing as Discharged"
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {error}
          </Alert>
        )}
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            px: 2,
            border: `1px dashed ${IPD_COLORS.borderHover}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <DescriptionIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
          <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
            No summary drafted yet
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
            {disabled
              ? "This visit closed without one."
              : "It is written from the visit's own record — the assessment, the complaint and what happened here — so you edit rather than start from a blank page."}
          </Typography>
          {!disabled && (
            <IpdButton
              startIcon={<AutoAwesomeIcon />}
              onClick={generate}
              loading={busy}
              sx={{ mt: 2 }}
            >
              Draft the summary
            </IpdButton>
          )}
        </Box>
      </IpdSectionCard>
    );
  }

  /* ----------------------------------------------------------- read mode */
  if (!editing) {
    return (
      <>
        <IpdSectionCard
          title="Discharge summary"
          subtitle={
            summary.edited
              ? `Edited by ${summary.updated_by_name || "unknown"} · ${formatDateTime(
                  summary.updated_at
                )}`
              : `Drafted from the record · ${formatDateTime(summary.generated_at)}`
          }
          actions={
            <Box sx={{ display: "flex", gap: 1 }}>
              <IpdButton
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={print}
              >
                Print
              </IpdButton>
              {!disabled && (
                <>
                  <IpdButton
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() =>
                      summary.edited ? setConfirmRegenerate(true) : generate()
                    }
                    disabled={busy}
                  >
                    Redraft
                  </IpdButton>
                  <IpdButton
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setDraft(draftFrom(summary));
                      setError("");
                      setEditing(true);
                    }}
                  >
                    Edit
                  </IpdButton>
                </>
              )}
            </Box>
          }
        >
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {!summary.edited && !disabled && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
              This is a draft written from the record. Read it through as the patient
              will, and edit anything that is not right before closing the visit.
            </Alert>
          )}

          <Box sx={{ display: "grid", gap: 1.75 }}>
            {SECTIONS.map((s) =>
              summary[s.key] ? (
                <Box key={s.key}>
                  <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: 13.5, whiteSpace: "pre-wrap" }}>
                    {summary[s.key]}
                  </Typography>
                </Box>
              ) : null
            )}
          </Box>
        </IpdSectionCard>

        <IpdConfirmDialog
          open={confirmRegenerate}
          destructive
          title="Redraft from the record?"
          description="Your edits to this summary will be discarded and it will be written again from the visit's record. There is no way back to the edited version."
          confirmLabel="Redraft and discard edits"
          loading={busy}
          onCancel={() => setConfirmRegenerate(false)}
          onConfirm={async () => {
            const ok = await generate();
            if (ok) setConfirmRegenerate(false);
          }}
        />
      </>
    );
  }

  /* ----------------------------------------------------------- edit mode */
  return (
    <IpdSectionCard
      title="Discharge summary"
      subtitle="Write it as the patient will read it — no abbreviations, no shorthand"
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 1.75 }}>
        {SECTIONS.map((s) => (
          <Box key={s.key}>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>{s.label}</Typography>
            <IpdTextField
              value={draft[s.key]}
              onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
              size="small"
              fullWidth
              multiline
              minRows={s.rows}
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": s.label }}
            />
          </Box>
        ))}
      </Box>

      {!anyContent && (
        <Typography sx={{ mt: 1.5, fontSize: 12, color: IPD_COLORS.textMuted }}>
          The patient takes this home — it cannot be left completely blank.
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <IpdButton
          variant="outlined"
          onClick={() => {
            setDraft(draftFrom(summary));
            setError("");
            setEditing(false);
          }}
          disabled={busy}
        >
          Cancel
        </IpdButton>
        <IpdButton onClick={save} loading={busy} disabled={!anyContent}>
          Save summary
        </IpdButton>
      </Box>
    </IpdSectionCard>
  );
}
